import React from 'react';
import { KYCFieldRow } from './KYCFieldRow';
export interface KYCField {
  id: string;
  label: string;
  selected: boolean;
  mandatory: boolean;
  optional: boolean;
  fieldType?: 'text' | 'dropdown' | 'number' | 'email' | 'phone' | 'date' | 'file';
  regex?: string;
  explanation?: string;
  dropdownOptions?: string[];
}
interface PersonalInfoTabProps {
  fields: KYCField[];
  onFieldChange: (id: string, updates: Partial<KYCField>) => void;
  onSelectAll: (selected: boolean) => void;
}
// Define field metadata with Data Type, Regex, and Explanation
const fieldMetadata: Record<string, {
  fieldType: KYCField['fieldType'];
  regex?: string;
  explanation?: string;
  dropdownOptions?: string[];
}> = {
  name: {
    fieldType: 'text',
    regex: '^[A-Za-z\\s]+$',
    explanation: 'Matches only alphabetic characters and spaces (for full names). Does not allow numbers or special characters.'
  },
  cnic: {
    fieldType: 'text',
    regex: '^\\d{5}-\\d{7}-\\d{1}$',
    explanation: 'CNIC follows the format 12345-1234567-1. This regex ensures the correct format with hyphens.'
  },
  cnic_expiry: {
    fieldType: 'date',
    regex: '^(0[1-9]|1[0-2])\\/\\d{4}$',
    explanation: 'Matches dates in the format MM/YYYY (e.g., 12/2025). Ensures the month is between 01 and 12 and the year is a four-digit number.'
  },
  gender: {
    fieldType: 'dropdown',
    regex: '^(Male|Female|Other)$',
    explanation: 'Matches one of the three values: Male, Female, or Other.',
    dropdownOptions: ['Male', 'Female', 'Other']
  },
  province: {
    fieldType: 'text',
    regex: '^[A-Za-z\\s]+$',
    explanation: 'Matches alphabetic characters and spaces (valid for city/province names). Does not allow numbers or special characters.'
  },
  city: {
    fieldType: 'text',
    regex: '^[A-Za-z\\s]+$',
    explanation: 'Matches alphabetic characters and spaces (valid for city/province names). Does not allow numbers or special characters.'
  },
  address: {
    fieldType: 'text',
    regex: '^[A-Za-z0-9\\s,.-]+$',
    explanation: 'Matches letters, numbers, spaces, commas, periods, and hyphens. This is typical for address formats.'
  },
  date_of_birth: {
    fieldType: 'date',
    regex: '^(0[1-9]|1[0-9]|2[0-9]|3[01])\\/(0[1-9]|1[0-2])\\/\\d{4}$',
    explanation: 'Matches date in the format DD/MM/YYYY and ensures the day, month, and year are valid.'
  }
};
export function PersonalInfoTab({
  fields,
  onFieldChange,
  onSelectAll
}: PersonalInfoTabProps) {
  const allSelected = fields.length > 0 && fields.every(f => f.selected);
  return <div className="space-y-6 py-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-430">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-431">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between" data-id="element-432">
          <div className="flex items-center" data-id="element-433">
            <input type="checkbox" checked={allSelected} onChange={e => onSelectAll(e.target.checked)} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer" data-id="element-434" />
            <span className="ml-4 text-sm font-semibold text-gray-700" data-id="element-435">
              Personal Identity and Demographic Details
            </span>
          </div>
          <div className="flex space-x-12 pr-6" data-id="element-436">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24 text-center" data-id="element-437">
              Configuration
            </span>
          </div>
        </div>

        <div className="divide-y divide-gray-100" data-id="element-438">
          {fields.map(field => {
          const metadata = fieldMetadata[field.id] || {
            fieldType: 'text'
          };
          return <KYCFieldRow key={field.id} label={field.label} isSelected={field.selected} isMandatory={field.mandatory} isOptional={field.optional} fieldType={metadata.fieldType} regex={metadata.regex} explanation={metadata.explanation} dropdownOptions={metadata.dropdownOptions} onToggleSelect={checked => onFieldChange(field.id, {
            selected: checked
          })} onToggleMandatory={checked => onFieldChange(field.id, {
            mandatory: checked,
            optional: checked ? false : field.optional
          })} onToggleOptional={checked => onFieldChange(field.id, {
            optional: checked,
            mandatory: checked ? false : field.mandatory
          })} data-id="element-439" />;
        })}
        </div>
      </div>
    </div>;
}